import type {
  HealthieTaskWorkItem,
  PrescriptionStage,
  PrescriptionWorkItem,
} from "@/types/clinician";

const SEARCHABLE_STAGE_LABELS: Record<PrescriptionStage, string> = {
  awaiting_id: 'Missing ID',
  ready_to_prescribe: "Ready to prescribe",
  approval_required: "Approval required",
  intake_required: "Check-in intake missing",
  sync_required: "Visit required",
  visit_scheduled: "Visit scheduled",
  prescribed: "Prescribed",
  rejected: "Rejected follow-up",
  missing_info: "Missing info",
};

export function matchesWorklistSearch(
  item: PrescriptionWorkItem,
  query: string,
) {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [
    item.patient.display_name,
    item.patient.email,
    item.order_number,
    item.product,
    item.medication,
    item.channel,
    SEARCHABLE_STAGE_LABELS[item.stage],
  ].some((value) => value?.toLocaleLowerCase().includes(normalized));
}

export function matchesTaskSearch(task: HealthieTaskWorkItem, query: string) {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [task.patient.display_name, task.patient.email, task.content].some(
    (value) => value?.toLocaleLowerCase().includes(normalized),
  );
}

function taskCreatedMs(task: HealthieTaskWorkItem) {
  if (!task.created_at) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(task.created_at);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/**
 * Overdue work first, then newest first. Healthie returns tasks in its own
 * insertion order, which buried freshly created Rx work under months-old rows.
 */
export function sortPrescriptionTasks(tasks: HealthieTaskWorkItem[]) {
  return [...tasks].sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    const byCreated = taskCreatedMs(b) - taskCreatedMs(a);
    if (byCreated !== 0) return byCreated;
    return (b.created_at ?? "").localeCompare(a.created_at ?? "");
  });
}

export function formatTaskCreated(createdAt: string | null | undefined) {
  if (!createdAt) return null;
  const parsed = Date.parse(createdAt);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
